<template>
  <div class="order-details fade-in">
    <!-- Header Section -->
    <ProductDetailsHeader
        title="Supplier Purchase Order"
        icon="bag-check"
        :main="headerMain"
    >
      <template #header-actions>
        <router-link
            v-if="order.purchase"
            :to="'/purchase/' + order.purchase.id"
            class="btn btn-outline-primary"
        >
          <i class="bi bi-cart-check me-1"></i> View Received Order
        </router-link>
        <select
            v-if="order.status !== 'complete' && orderProducts.data.length > 0"
            class="form-select w-auto"
            v-model="orderStatus"
            @change="confirmDialog(
                  getConfirmTitle,
                  updateOrderStatus,
                  'Are you sure you want to change order status to ' + orderStatus
                )" data="">
          <option value="" disabled selected>Change Status</option>
          <option v-if="order.status === 'pending'" value="submitted" class="text-primary fs-5">Submit Order</option>
          <option v-if="order.status === 'submitted'" value="pending" class="text-warning fs-5">Edit Order</option>
          <option v-if="order.status === 'submitted'" value="complete" class="text-success fs-5">Approve Order</option>
        </select>
      </template>
    </ProductDetailsHeader>

    <!-- Available Products Section (when status is pending) -->
    <StoreProducts
        v-if="order.status === 'pending'"
        :per-page="5"
        :marked-products="orderProducts"
        @row-click="populateAddProduct"
    />

    <!-- Order Products Section -->
    <ProductDetailsPages
        title="Purchase Order"
        pdf-title="LOCAL PURCHASE ORDER"
        :hide-pricing-fields="true"
        :loading="loading"
        :detail-products="orderProducts"
        :current-page="detailsPage"
        :current-per-page="detailsPerPage"
        :current-search="detailsSearch"
        @update:page="onDetailsPageChange"
        @update:per-page="onDetailsPerPageChange"
        @update:search="onDetailsSearchChange"
        :main="headerMain"
        @edit-product="populateOrderEdit"
        @delete-product="deleteOrderProduct"
    >
      <template #footer-actions>
        <div v-if="order.status !== 'complete' && orderProducts.data.length > 0">
          <select
              class="form-select status-select"
              v-model="orderStatus"
              @change="confirmDialog(
                  getConfirmTitle,
                  updateOrderStatus,
                  'Are you sure you want to change order status to ' + orderStatus
                )" data="">
            <option value="" disabled selected>Change Status</option>
            <option v-if="order.status === 'pending'" value="submitted" class="text-primary fs-5">Submit Order</option>
            <option v-if="order.status === 'submitted'" value="pending" class="text-warning fs-5">Edit Order</option>
            <option v-if="order.status === 'submitted'" value="complete" class="text-success fs-5">Approve Order</option>
          </select>
        </div>
      </template>
    </ProductDetailsPages>

    <!-- Add/Edit Product Modal -->
    <Modal :open="addProductModal" @closed="addProductModal = false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0" v-if="addProductMode === 'add'">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Product to Purchase Order
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Purchase Order Product
        </h5>
        <button @click="addProductModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="form-group col-md-6">
              <label for="name" class="form-label fw-medium mb-2">Product Name:</label>
              <input type="text" class="form-control" :value="selectedProduct.name" disabled>
            </div>
            <div class="form-group col-md-6">
              <label for="quantity" class="form-label fw-medium mb-2">Quantity:</label>
              <input type="number" class="form-control" v-model="orderDetails.quantity" min="1">
            </div>
            <div class="form-group col-md-12 mt-3">
              <label for="comment" class="form-label fw-medium mb-2">Comment: <span class="text-muted fst-italic small">(optional)</span></label>
              <input type="text" class="form-control" v-model="orderDetails.comment" placeholder="Add any special instructions or notes">
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="addProductModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" v-if="addProductMode === 'add'" @click="addOrderProduct">
          <i class="bi bi-plus-lg me-1"></i> Add Product
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateOrderProduct()">
          <i class="bi bi-check-lg me-1"></i> Update Product
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import ProductDetailsHeader from "@/components/details/ProductDetailsHeader.vue";
import ProductDetailsPages from "@/components/details/ProductDetailsPages.vue";
import StoreProducts from "@/components/store/StoreProducts.vue";
import orderService from "@/api/orderService";
import confirmDialog  from "@/utils/confirmDialog";

export default {
  name: "OrderDetails",

  components: {ProductDetailsHeader, ProductDetailsPages, StoreProducts},

  data(){
    return {
      loading: true,
      orderStatus: '',
      order: {
        supplier: {},
        user: {},
      },
      orderDetails: {
        order_id: this.$route.params.id,
        product_id: '',
        quantity: 1
      },
      selectedProduct: {},
      orderProducts: { data: [] },
      detailsPage: 1,
      detailsPerPage: 20,
      detailsSearch: '',
      addProductModal: false,
      addProductMode: 'add',
    }
  },

  methods:{
    confirmDialog,
    async updateOrderStatus() {
      await orderService.changeOrderStatus({status: this.orderStatus}, this.$route.params.id)
    },
    async getOrderDetails() {
      this.orderProducts = await orderService.getOrderDetails(this.$route.params.id, {
        page: this.detailsPage,
        per_page: this.detailsPerPage
      })
      this.loading = false
    },
    onDetailsPageChange(page){
      this.detailsPage = page;
      this.getOrderDetails();
    },
    onDetailsPerPageChange(perPage){
      this.detailsPerPage = perPage;
      this.detailsPage = 1;
      this.getOrderDetails();
    },
    onDetailsSearchChange(search){
      this.detailsSearch = search;
      this.detailsPage = 1;
      this.getOrderDetails();
    },
    populateAddProduct(storeProduct) {
      this.selectedProduct.name = storeProduct.product.name
      this.orderDetails.product_id = storeProduct.product_id
      this.orderDetails.price = storeProduct.price
      this.addProductModal = true;
      this.addProductMode = 'add';
    },
    async addOrderProduct() {
      let response = await orderService.createOrderDetails(this.orderDetails)
      if (response) {
        this.addProductModal = false
        await this.getOrderDetails()
      }
    },
    async updateOrderProduct() {
      let response = await orderService.updateOrderDetails(this.orderDetails, this.orderDetails.id)
      if (response) {
        this.addProductModal = false
        await this.getOrderDetails()
      }
    },
    populateOrderEdit(order) {
      this.orderDetails.id = order.id
      this.selectedProduct.name = order.product.name
      this.orderDetails.product_id = order.product_id
      this.orderDetails.price = order.price
      this.orderDetails.quantity = order.quantity
      this.orderDetails.comment = order.comment
      this.addProductModal = true;
      this.addProductMode = 'edit';
    },
    async deleteOrderProduct(id) {
      let response = await orderService.deleteOrderDetails(id)
      if (response) {
        await this.getOrderDetails()
      }
    },
  },

  computed: {
    headerMain() {
      return {
        ...this.order,
        store: this.order.supplier
      }
    },
    getConfirmTitle() {
      switch (this.orderStatus) {
        case 'submitted':
          return 'Submit Purchase Order'
        case 'pending':
          return 'Edit Purchase Order'
        case 'complete':
          return 'Approve Purchase Order'
      }
    }
  },

  created: async function () {
    this.order = await orderService.getOrder(this.$route.params.id)
    await this.getOrderDetails()
  },

  watch: {
    addProductModal: function () {
      if (!this.addProductModal) {
        this.orderDetails = {
          order_id: this.$route.params.id,
          quantity: 1
        }
      }
    }
  }

}
</script>

<style scoped>
</style>
